import { useFormBehaviors } from 'hooks';
import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useUserProfilePageDataContext } from 'pages/Settings/pages/UserProfile/UserProfilePage.context';

import { useDispatch, useSelector } from 'react-redux';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { userSelector } from 'store/selectors/userSelectors/userSelector';
import {
  API_PUT_agencyUserProfile,
  API_PUT_b2bUserProfile,
  UserProfilePageForm,
} from 'pages/Settings/pages/UserProfile/UserProfilePage.constants';
import { useTranslation } from 'hooks/useTranslation';
import {
  AgencyUser,
  B2bCustomer,
  TimeFormatEnum,
  UserTimeFormatConfigurationEnum,
} from 'types/graphql';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useGeneralSettingsDataContext } from 'pages/Settings/pages/Organization/GeneralSettingsPage/GeneralSettingsPage.data.context';
import { SessionService } from 'services';
import { authSelector } from 'store/selectors/userSelectors/userSelector';
import { defaultApolloClient } from 'providers/ApolloProvider';
import { fetchMeData } from 'store/reducers/auth/authReducer.constants';

export const UserProfilePageFormContext = createContext(
  {} as UseFormBehaviors<UserProfilePageForm>,
);

export const UserProfilePageFormProvider: FC = ({ children }) => {
  const { data, fetch } = useUserProfilePageDataContext();
  const { apiRequest } = useAPIActions();
  const isUserAgency = useSelector(isUserAgencySelector);
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const auth = useSelector(authSelector);
  const { t } = useTranslation();
  const orgData = useGeneralSettingsDataContext();

  const meData = useMemo(
    () =>
      !!data && !!data?.me
        ? { ...(data.me as B2bCustomer | AgencyUser) }
        : ({} as B2bCustomer | AgencyUser),
    [data],
  );

  const timeFormatConfiguration = useMemo(() => {
    if (meData.timeFormatConfiguration === UserTimeFormatConfigurationEnum.military) {
      return true;
    }
    if (meData.timeFormatConfiguration === UserTimeFormatConfigurationEnum.standard) {
      return false;
    }
    if (meData.timeFormatConfiguration === UserTimeFormatConfigurationEnum.org_default) {
      const orgTimeFormat = orgData?.data?.fetchCommunity?.organization?.timeFormatConfiguration;
      return orgTimeFormat === TimeFormatEnum.military;
    }
    if (user?.time_format) {
      return user.time_format === TimeFormatEnum.military;
    }
    return false;
  }, [
    meData.timeFormatConfiguration,
    orgData?.data?.fetchCommunity?.organization?.timeFormatConfiguration,
    user?.time_format,
  ]);
  const apiRequestConfig = useMemo(() => {
    return isUserAgency ? API_PUT_agencyUserProfile(meData.id) : API_PUT_b2bUserProfile(meData.id);
  }, [isUserAgency, meData]);

  const handleSubmit = useCallback(
    async (formValues: UserProfilePageForm) => {
      const { time_format_configuration, ...rest } = formValues;
      const apiPayload = {
        ...rest,
        time_format_configuration: time_format_configuration ? 'military' : 'standard',
      } as unknown as UserProfilePageForm;
      await apiRequest(apiRequestConfig(apiPayload), {
        onSuccess: async () => {
          const newTimeFormat = time_format_configuration
            ? TimeFormatEnum.military
            : TimeFormatEnum.standard;

          if (auth?.baseUrl && user) {
            try {
              const updatedUserData = await fetchMeData({ baseUrl: auth.baseUrl });
              const resolvedTimeFormat =
                (updatedUserData.time_format as TimeFormatEnum) || newTimeFormat;

              dispatch({
                type: 'auth/updateUserTimeFormat',
                payload: {
                  time_format: resolvedTimeFormat,
                },
              });

              SessionService.updateTimeFormat(resolvedTimeFormat);
            } catch (error) {
              dispatch({
                type: 'auth/updateUserTimeFormat',
                payload: {
                  time_format: newTimeFormat,
                },
              });

              SessionService.updateTimeFormat(newTimeFormat);
            }
          } else if (user) {
            dispatch({
              type: 'auth/updateUserTimeFormat',
              payload: {
                time_format: newTimeFormat,
              },
            });

            SessionService.updateTimeFormat(newTimeFormat);
          }

          await defaultApolloClient.refetchQueries({ include: ['USER_PROFILE_PAGE'] });

          toastService.success(t('settings:UPDATED', { value: formValues.email }));
          fetch();
        },
        onError: handleGenericErrorMessage,
      });
    },
    [apiRequest, apiRequestConfig, fetch, t, dispatch, user, auth?.baseUrl],
  );

  const form = useFormBehaviors<UserProfilePageForm>({
    initialState: {
      first_name: meData.firstName,
      last_name: meData.lastName,
      email: meData.email,
      phone: meData.phone ?? undefined,
      locale: meData.locale,
      time_format_configuration: timeFormatConfiguration,
    },
    onSubmit: handleSubmit,
  });

  const contextValue = useMemo(() => form, [form]);

  return (
    <UserProfilePageFormContext.Provider value={contextValue}>
      {children}
    </UserProfilePageFormContext.Provider>
  );
};

export const useUserProfilePageFormContext = () => useContext(UserProfilePageFormContext);
